import { useFetch } from "modules/main/hooks/useFetch";
import { forwardRef, useState } from "react"
import { KPI,KPIBase} from "modules/checkout/components/kpi-dashboard";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Gauge, PiggyBank, Receipt} from "lucide-react";
import { PurchaseData, UserDashboard } from "modules/main/types/user-dashboard";
import { Progress } from "@/components/ui/progress";

import { cn } from "@/lib/utils";
import { dashboardEndPoint } from "modules/main/services/endpoints";
import { FetchDataOptions } from "modules/main/services/fetchData";

import React from "react";
import Amount from "utils/amount";
import { differenceInDays } from "date-fns";
import { useAuth } from "modules/authentication/hooks/use-auth";
import { AcquirePlusDialog } from "modules/subscription/components/AcquirePlusDialog";



interface UserDashboardProps extends React.HTMLAttributes<HTMLDivElement>{

}

/**
 * Computes the fill percentage for a quota bar.
 *
 * @param usage - Current usage value.
 * @param limit - Quota limit value.
 * @returns Rounded 0-100 percentage; 0 when the limit is zero or negative.
 */
const computeQuotaPercent = (usage: number, limit: number): number => {
  if (limit <= 0) return 0;
  return Math.min(Math.round((usage / limit) * 100), 100);
};

export const UsersDashboard = forwardRef<HTMLDivElement,UserDashboardProps>(({...props},ref) => {
    const { isPremiumUser } = useAuth();
    const [isAcquirePlusOpen, setIsAcquirePlusOpen] = useState(false);
    const requestOptions:FetchDataOptions = {
        method:"GET",
        headers:{"content-type":"application/json"}
    }
    
    const {data: dashboardData,error: dashboardError} = useFetch<UserDashboard>(dashboardEndPoint,requestOptions);
    if(dashboardError || !dashboardData){
      return (<></>);
    }

    const shouldShowQuotaProgress = !isPremiumUser && dashboardData.quotaUsage;
    const shouldShowPurchaseKpi = dashboardData.lastPurchase > 0 || dashboardData.totalOptimizedPurchases > 0;
    const monthlySavingsPercent = dashboardData.quotaUsage
      ? computeQuotaPercent(dashboardData.quotaUsage.monthlySavings, dashboardData.quotaUsage.savingsLimit)
      : 0;
    const remainingWeeklyListQuotes = dashboardData.quotaUsage
      ? Math.max(dashboardData.quotaUsage.weeklyListQuotesLimit - dashboardData.quotaUsage.weeklyListQuotes, 0)
      : 0;
    const remainingMonthlyOptimizations = dashboardData.quotaUsage
      ? Math.max(dashboardData.quotaUsage.monthlyOptimizationLimit - dashboardData.quotaUsage.monthlyOptimizations, 0)
      : 0;

    const lastPurchaseDays = differenceInDays(new Date(), new Date(dashboardData.lastPurchaseDate));
    const lastPurchaseText = lastPurchaseDays === 0 ? "hoy" : `hace ${lastPurchaseDays} día${lastPurchaseDays === 1 ? "" : "s"}`;

    return (
    <div className="flex flex-col gap-4 md:mx-0">                
        <div className="flex flex-col md:flex-row w-full gap-4">
            <KPI icon={PiggyBank} 
              className="w-full md:flex-1 bg-[#5A9E71] text-white max-w-96" 
              iconClassName="text-white"
              title="Llevas ahorrado" 
              subText="este mes"
              subTitle={
                <div className="flex flex-col gap-4">
                  {dashboardData.cummulateSaving > 0 && (
                  <div className="flex gap-2 font-semibold text-white text-xs">
                    <span>Este año llevas </span>
                    <Amount
                      amount={dashboardData.cummulateSaving}
                      currency="CLP"
                      locale="es-CL"
                      className="font-extrabold"
                    />                    
                  </div>
                  )}
                  { dashboardData.cummulateSaving === 0 && (
                    <p className="text-sm text-white/80 text-center font-semibold">
                     Optimiza una lista y comienza a ahorrar 
                    </p>
                  ) }
                  {shouldShowQuotaProgress && dashboardData.cummulateSaving > 0 && (
                    <div className="flex flex-col gap-2 text-white">
                      <div className="flex justify-end text-xs font-medium">
                        <span>
                          <Amount amount={dashboardData.quotaUsage.monthlySavings} currency="CLP" locale="es-CL" />
                          <span> / </span>
                          <Amount amount={dashboardData.quotaUsage.savingsLimit} currency="CLP" locale="es-CL" />
                        </span>
                      </div>
                      <Progress value={monthlySavingsPercent} className="bg-white/30" />
                      <p className="text-xs text-white/80 text-center">
                        Con el plan gratuito ahorra hasta{" "}
                        <Amount amount={dashboardData.quotaUsage.savingsLimit} currency="CLP" locale="es-CL"  className="font-semibold"/> al mes.
                        Pasado este puedes continuar con <span className="font-semibold">Carriapp Plus</span>.
                      </p>
                    </div>
                  )}
                </div>
              } 
              amount={dashboardData.monthlyTotalSave}/>
             {shouldShowPurchaseKpi && (
             <KPI icon={Receipt} 
              className="w-full md:flex-1 max-w-96" 
              iconClassName=""
              title="Tu última compra"
              subText={lastPurchaseText}
              subTitle={
                <div className="flex gap-2 font-semibold">
                  <span className="text-primary font-bold">{dashboardData.totalOptimizedPurchases}</span>
                  <span>{dashboardData.totalOptimizedPurchases === 1 ? "optimización" : "optimizaciones"}</span>
                </div>
              } 
              amount={dashboardData.lastPurchase}/>
             )}
             {shouldShowQuotaProgress && (
             <KPIBase icon={Gauge} 
              className="w-full md:flex-1 max-w-96" 
              iconClassName=""
              title="Tu plan gratuito"
            >
              <div className="flex flex-col gap-3">
                <div className="flex flex-col gap-1 text-sm font-semibold">
                  <span>
                    {remainingWeeklyListQuotes === 0 ? (
                      "Ya usaste las cotizaciones de esta semana"
                    ) : (
                      <>
                        Puedes cotizar <span className="text-primary font-bold">{remainingWeeklyListQuotes}</span>{" "}
                        {remainingWeeklyListQuotes === 1 ? "lista más" : "listas más"} esta semana
                      </>
                    )}
                  </span>
                  <span>
                    {remainingMonthlyOptimizations === 0 ? (
                      "Ya usaste las optimizaciones de este mes"
                    ) : (
                      <>
                        Puedes optimizar <span className="text-primary font-bold">{remainingMonthlyOptimizations}</span>{" "}
                        {remainingMonthlyOptimizations === 1 ? "lista más" : "listas más"} este mes
                      </>
                    )}
                  </span>
                </div>
                <div className="flex flex-col gap-1">
                  <Button
                    className="w-full font-semibold border-primary"
                    onClick={() => setIsAcquirePlusOpen(true)}
                    size="lg"
                    variant={"default"}
                  >
                    Continuar con +Plus
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">Ahorra sin límites</p>
                </div>
              </div>
            </KPIBase>
             )}
        </div>
        {isAcquirePlusOpen && (
          <AcquirePlusDialog open={isAcquirePlusOpen} onOpenChange={setIsAcquirePlusOpen} />
        )}
    </div>
    );
});


interface OrderHistoryProps extends React.HTMLAttributes<HTMLDivElement> {
    lastPurchases:PurchaseData[]
    isDesktop?:boolean
}

export const OrderHistory = forwardRef<HTMLDivElement,OrderHistoryProps>(({lastPurchases,isDesktop,...props},ref)=>{
    return (
            <Card className={cn("flex flex-col ",props.className)}>
            <CardHeader className={"flex flex-row items-center justify-between space-y-0 pb-2 gap-2"} >
                <CardTitle className="text-xs md:text-sm font-medium">Últimos pedidos</CardTitle>                
            </CardHeader>
            <CardContent>
            {/* {isDesktop?(
                <OrderHistoryTable lastPurchases={lastPurchases}/>
            ):(
                <OrderHistoryCard lastPurchases={lastPurchases}/>
            )}                 */}
            </CardContent>
        </Card>  
    );
})
